import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trophy, Plus, Trash, Warning, CheckCircle, X, ArrowsClockwise, Medal } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const LEVELS = [
  ["jeux_martiaux", "🥋 Jeux Martiaux"],
  ["inter_ecole", "Inter-écoles (développement)"],
  ["regional", "Régional"],
  ["ontario_ottawa", "Ontario / Ottawa"],
  ["provincial", "Qualification provinciale"],
  ["national", "National"],
  ["international", "International"],
];
const DISCIPLINES = ["muay_thai", "kickboxing", "jujutsu_japonais", "bjj"];

const EMPTY = {
  name: "", organizer: "", location: "", date: "", start_time: "", end_time: "",
  level: "inter_ecole", disciplines: [], registration_deadline: "", estimated_fees: 0,
  official_url: "", travel_hours: 0, notes: "",
  lead_coach: "", assistant_coach: "", dojo_instructor: "", replacement_plan: "",
  workflow_status: "idea",
};

const TONE = {
  go: { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-300" },
  caution: { bg: "bg-amber-500/15", border: "border-amber-500/40", text: "text-amber-300" },
  stop: { bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-300" },
};
const COVERAGE_LABEL = { covered: "✅ Couvert", partial: "⚠️ Partiel", not_covered: "⛔ Non couvert" };
const CONFLICT_LABEL = { low: "🟢 Faible", moderate: "🟡 Modéré", high: "🔴 Élevé" };

export default function CompetitionModule({ embedded = false }) {
  const [events, setEvents] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [e, c] = await Promise.all([
        api.get("/competition/events"),
        api.get("/competition/coaches"),
      ]);
      setEvents(e.data.events || []);
      setCoaches(c.data.coaches || []);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
  const toggleDisc = (d) => setEditing((p) => ({
    ...p, disciplines: p.disciplines.includes(d) ? p.disciplines.filter((x) => x !== d) : [...p.disciplines, d],
  }));

  const save = async () => {
    if (!editing.name?.trim() || !editing.date) { toast.error("Nom et date requis"); return; }
    setSaving(true);
    try {
      const body = { ...editing, estimated_fees: parseFloat(editing.estimated_fees) || 0, travel_hours: parseFloat(editing.travel_hours) || 0 };
      if (editing.id) await api.put(`/competition/events/${editing.id}`, body);
      else await api.post("/competition/events", body);
      toast.success("Événement enregistré");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    try { await api.delete(`/competition/events/${id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className={embedded ? "space-y-6" : "space-y-6 p-6 md:p-8"} data-testid="competition-module">
      {embedded ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-zinc-400">Planification des déplacements — conflits d'horaire &amp; couverture instructeur.</div>
          <div className="flex gap-2">
            <button onClick={load} disabled={loading} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="comp-refresh"><ArrowsClockwise size={16} className={loading ? "animate-spin" : ""} /></button>
            <button onClick={() => setEditing({ ...EMPTY })} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 text-sm font-bold flex items-center gap-2 active:scale-[0.98]" data-testid="comp-new-event-btn"><Plus size={16} /> Nouvel événement</button>
          </div>
        </div>
      ) : (<>
      <PageHeader
        title="Jeux Martiaux"
        icon={Medal}
        breadcrumbs={[{ label: "Jeux Martiaux & événements" }, { label: "Jeux Martiaux" }]}
        subtitle="« Jeux Martiaux » : événement inter-écoles multidisciplinaire — Samedi le 14 novembre 2026. Le système vérifie les conflits d'horaire et exige une couverture instructeur avant d'approuver un déplacement."
        actions={
          <>
            <button onClick={load} disabled={loading} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="comp-refresh">
              <ArrowsClockwise size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={() => setEditing({ ...EMPTY })} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 text-sm font-bold flex items-center gap-2 active:scale-[0.98]" data-testid="comp-new-event-btn">
              <Plus size={16} /> Nouvel événement
            </button>
          </>
        }
      />

      {/* Concept « Jeux Martiaux » */}
      <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.06] p-4" data-testid="jeux-martiaux-concept">
        <div className="flex items-center gap-2 text-sky-300 font-bold text-sm mb-2">
          <Medal size={18} weight="fill" /> Jeux Martiaux Inter-Écoles — Samedi le 14 novembre 2026
        </div>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-zinc-300">
          <li>🥋 <b>Multidisciplinaire</b> — Muay Thaï · Kickboxing · Jiu-jitsu · Can-Ryu, plusieurs plateaux.</li>
          <li>🤝 <b>Inter-écoles</b> — le groupe fait progresser tes 2-3 athlètes à fort potentiel.</li>
          <li>🎖️ <b>Prix spéciaux permis</b> — assiduité, entraide, meilleure performance (certificats de gala).</li>
        </ul>
      </div>
      </>)}

      {loading && !events.length ? (
        <div className="text-zinc-500 text-sm py-10 text-center">Chargement…</div>
      ) : events.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-10 text-center" data-testid="comp-empty">
          <Medal size={40} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">Aucun événement. Crée tes premiers Jeux Martiaux (ex. inter-écoles multidisciplinaire en Abitibi).</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {events.map((ev) => {
            const tone = TONE[ev.tone] || TONE.caution;
            return (
              <div key={ev.id} className={`rounded-xl border ${tone.border} bg-zinc-900 p-4`} data-testid={`comp-event-${ev.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-white font-bold truncate">{ev.name}</div>
                    <div className="text-xs text-zinc-500">{ev.level_label} · {ev.date}{ev.location ? ` · ${ev.location}` : ""}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setEditing({ ...EMPTY, ...ev })} className="text-xs px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200" data-testid={`comp-edit-${ev.id}`}>Modifier</button>
                    <button onClick={() => remove(ev.id)} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`comp-del-${ev.id}`}><Trash size={15} /></button>
                  </div>
                </div>

                <div className={`mt-3 rounded-lg ${tone.bg} ${tone.text} px-3 py-2 text-sm font-semibold flex items-center gap-2`} data-testid={`comp-reco-${ev.id}`}>
                  {ev.tone === "go" ? <CheckCircle size={16} weight="fill" /> : <Warning size={16} weight="fill" />} {ev.recommendation_label}
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  <div className="bg-zinc-950 rounded-lg p-2">
                    <div className="text-zinc-500">Conflit horaire</div>
                    <div className="text-zinc-200 font-medium">{CONFLICT_LABEL[ev.conflict_level]}</div>
                    <div className="text-zinc-600 text-[10px] mt-0.5">{ev.conflicting_classes?.length || 0} cours régulier(s) ce jour-là</div>
                  </div>
                  <div className="bg-zinc-950 rounded-lg p-2">
                    <div className="text-zinc-500">Couverture dojo</div>
                    <div className="text-zinc-200 font-medium">{COVERAGE_LABEL[ev.coverage_status]}</div>
                    <div className="text-zinc-600 text-[10px] mt-0.5 truncate">{ev.dojo_instructor ? `Reste : ${ev.dojo_instructor}` : "Personne assigné"}</div>
                  </div>
                </div>

                {ev.conflicting_classes?.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-amber-400 cursor-pointer">Voir les cours en conflit</summary>
                    <ul className="mt-1 text-[11px] text-zinc-400 space-y-0.5">
                      {ev.conflicting_classes.map((c, i) => (
                        <li key={i}>• {c.heure} — {c.titre} {c.instructeur ? `(${c.instructeur})` : ""}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Form modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="comp-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">{editing.id ? "Modifier l'événement" : "Nouvel événement"}</h2>
              <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nom *"><input className="inp" value={editing.name} onChange={(e) => setF("name", e.target.value)} data-testid="comp-name" /></Field>
              <Field label="Organisateur"><input className="inp" value={editing.organizer} onChange={(e) => setF("organizer", e.target.value)} /></Field>
              <Field label="Lieu"><input className="inp" value={editing.location} onChange={(e) => setF("location", e.target.value)} /></Field>
              <Field label="Date *"><input type="date" className="inp" value={editing.date} onChange={(e) => setF("date", e.target.value)} data-testid="comp-date" /></Field>
              <Field label="Niveau">
                <select className="inp" value={editing.level} onChange={(e) => setF("level", e.target.value)} data-testid="comp-level">
                  {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="Heures de déplacement (aller)"><input type="number" step="0.5" className="inp" value={editing.travel_hours} onChange={(e) => setF("travel_hours", e.target.value)} /></Field>
              <Field label="Date limite d'inscription"><input type="date" className="inp" value={editing.registration_deadline} onChange={(e) => setF("registration_deadline", e.target.value)} /></Field>
              <Field label="Frais estimés ($)"><input type="number" className="inp" value={editing.estimated_fees} onChange={(e) => setF("estimated_fees", e.target.value)} /></Field>
              <Field label="Lien officiel"><input className="inp" value={editing.official_url} onChange={(e) => setF("official_url", e.target.value)} /></Field>
            </div>
            <div className="mt-3">
              <div className="text-xs text-zinc-400 mb-1">Disciplines</div>
              <div className="flex flex-wrap gap-2">
                {DISCIPLINES.map((d) => (
                  <button key={d} onClick={() => toggleDisc(d)} className={`text-xs px-3 py-1 rounded-full border ${editing.disciplines.includes(d) ? "bg-amber-500/20 border-amber-500/50 text-amber-200" : "bg-zinc-950 border-zinc-700 text-zinc-400"}`}>{d}</button>
                ))}
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-zinc-800">
              <div className="text-xs font-semibold text-amber-300 mb-2">🛡️ Couverture dojo (obligatoire pour approuver)</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Coach principal (part)"><CoachSelect coaches={coaches} value={editing.lead_coach} onChange={(v) => setF("lead_coach", v)} testid="comp-lead" /></Field>
                <Field label="Coach assistant (part)"><CoachSelect coaches={coaches} value={editing.assistant_coach} onChange={(v) => setF("assistant_coach", v)} /></Field>
                <Field label="Instructeur qui RESTE au dojo"><CoachSelect coaches={coaches} value={editing.dojo_instructor} onChange={(v) => setF("dojo_instructor", v)} testid="comp-dojo" /></Field>
              </div>
              <Field label="Plan de remplacement des cours affectés">
                <textarea className="inp" rows={2} value={editing.replacement_plan} onChange={(e) => setF("replacement_plan", e.target.value)} placeholder="Ex. Nancy couvre le cours de 18h, BJJ reporté…" data-testid="comp-plan" />
              </Field>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm">Annuler</button>
              <button onClick={save} disabled={saving} className="px-5 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold disabled:opacity-50" data-testid="comp-save">{saving ? "…" : "Enregistrer"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CoachSelect({ coaches, value, onChange, testid }) {
  return (
    <select className="inp" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">—</option>
      {coaches.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
      {value && !coaches.find((c) => c.name === value) && <option value={value}>{value}</option>}
    </select>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-zinc-400 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
